import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Pencil, AlertTriangle, Heart, Link2, CheckCircle } from "lucide-react";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";

interface ChildCardProps {
  child: any;
  photoUrl?: string;
  formatAge: (dob: string | null) => string;
  onEdit: (child: any) => void;
  onDelete: (id: string) => Promise<void>;
  onViewProfile: (child: any) => void;
  onQuickAttendance: (child: any) => void;
  todayAttendance: Set<string>;
}

const ChildCard = ({ child, photoUrl, formatAge, onEdit, onDelete, onViewProfile, onQuickAttendance, todayAttendance }: ChildCardProps) => {
  const isPresent = todayAttendance.has(child.id);

  return (
    <Card className="shadow-card hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div
            className="flex items-center gap-3 cursor-pointer hover:opacity-80"
            onClick={() => onViewProfile(child)}
          >
            <Avatar className="w-12 h-12">
              <AvatarImage src={photoUrl || ""} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {child.full_name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h4 className="font-semibold text-foreground text-sm">{child.full_name}</h4>
              <p className="text-xs text-muted-foreground">{formatAge(child.date_of_birth)}</p>
            </div>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(child)}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <DeleteConfirmDialog
              title="حذف الطفل"
              description={`هل أنت متأكد من حذف "${child.full_name}"؟`}
              onConfirm={() => onDelete(child.id)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            {child.classes?.name ? (
              <Badge variant="outline" className="text-xs">{child.classes.name}</Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-muted-foreground border-dashed">غير محدد</Badge>
            )}
            {child.health_notes ? (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-200 bg-amber-50">
                <AlertTriangle className="w-3 h-3 ml-1" />صحي
              </Badge>
            ) : (
              <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200 bg-emerald-50">
                <Heart className="w-3 h-3 ml-1" />سليم
              </Badge>
            )}
          </div>

          {child.parent_name && (
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              {child.parent_id && <Link2 className="w-3 h-3 text-primary" />}
              {child.parent_name}
            </div>
          )}

          <Button
            variant={isPresent ? "secondary" : "outline"}
            size="sm"
            className="w-full text-xs h-7"
            onClick={() => onQuickAttendance(child)}
            disabled={isPresent}
          >
            <CheckCircle className={`w-3 h-3 ml-1 ${isPresent ? "text-emerald-500" : ""}`} />
            {isPresent ? "تم تسجيل الحضور" : "تسجيل حضور"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChildCard;
